import { useCallback, useEffect, useRef } from "react";
import { useRecoilState, useRecoilValue } from "recoil";

import { strings } from "@quickpoint.me/shared";
import Viewerati from "@quickpoint.me/viewerati";
import { HostId } from "../../state/atoms";
import { InPresentationModeSelector } from "../../state/selectors";
import { StyledPreview } from "./PreviewPane";

function LandscapeView({
	showLandscapePreview,
}: { showPortraitPreview: boolean; showLandscapePreview: boolean }) {
	const hostId = useRecoilValue(HostId);
	const ref = useRef<HTMLDivElement>(null);
	const [inPresentationMode, setInPresentationMode] = useRecoilState(
		InPresentationModeSelector,
	);

	const handleFullScreenChange = useCallback(() => {
		if (!document.fullscreenElement) {
			setInPresentationMode(false);
		}
	}, [setInPresentationMode]);

	/* the inline preview is a visual mirror of the editor: keep it out of the
	   tab order and screen reader scope (slide content is available as editor
	   text, and full-size in the pop-out accessible preview). In presentation
	   mode this same element goes fullscreen and must become interactive.
	   React 18 has no `inert` prop, so set the DOM property directly. */
	useEffect(() => {
		if (ref.current) {
			ref.current.inert = !inPresentationMode;
		}
	}, [inPresentationMode]);

	useEffect(() => {
		if (inPresentationMode && ref.current) {
			// A small delay allows the component to settle before animating, reducing jank.
			setTimeout(() => {
				ref.current?.requestFullscreen();
			}, 50);
		}
	}, [inPresentationMode]);

	useEffect(() => {
		const currentRef = ref.current;
		if (currentRef) {
			currentRef.addEventListener("fullscreenchange", handleFullScreenChange);
		}

		return () => {
			if (currentRef) {
				currentRef.removeEventListener(
					"fullscreenchange",
					handleFullScreenChange,
				);
			}
		};
	}, [handleFullScreenChange]);

	return (
		<StyledPreview
			ref={ref}
			key="landscape-preview-pane"
			id="landscape-preview-pane"
			data-testid="landscape-preview-pane"
			$show={showLandscapePreview}
			className="landscape"
			aria-hidden={inPresentationMode ? undefined : true}
		>
			<div
				id={strings.css.landscapeViewerID}
				className={`landscape-viewer ${strings.css.previewPane}`}
			>
				<Viewerati
					key={strings.css.landscapeViewerID}
					hostId={hostId}
					containerSelector={strings.css.landscapeViewerID}
					isInPopOut={false}
					isInAuthor={!inPresentationMode}
					isPreviewer={false}
				/>
			</div>
		</StyledPreview>
	);
}

export default LandscapeView;
